import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useState } from 'react';
import { useMutation, useQuery } from 'urql';
import DestructiveAction, {
  DestructiveActionDescription,
  DestructiveActionHeading,
} from '@/components/Custom/DestructiveAction';
import ContentBoundary from '@/components/UI/ContentBoundary';
import EmptyPlaceholder from '@/components/UI/EmptyPlaceholder';
import { graphql } from '@/gql/graphql';

const DeleteTeam = graphql(/* GraphQL */ `
  mutation DeleteTeam($teamId: uuid!) {
    delete_team_by_pk(id: $teamId) {
      id
      root_event_id
    }
  }
`);

const GetDeleteContext = graphql(`
  query GetTeamDeleteContext($teamId: uuid!) {
    team_by_pk(id: $teamId) {
      id
      title
    }
  }
`);

export default function TeamDelete() {
  const { teamId } = useLocalSearchParams<{ teamId: string }>();
  const { dismissTo } = useRouter();
  const [, remove] = useMutation(DeleteTeam);
  const [result, refresh] = useQuery({
    query: GetDeleteContext,
    variables: { teamId },
  });
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const entity = result.data?.team_by_pk;

  const removeEntity = async () => {
    const result = await remove({ teamId });
    if (result.error || !result.data?.delete_team_by_pk)
      return { success: false };
    setOrganizationId(result.data.delete_team_by_pk.root_event_id);
    return { success: true };
  };

  return (
    <>
      <Stack.Screen
        options={{ title: entity?.title ? `Delete ${entity.title}` : 'Delete' }}
      />
      <ContentBoundary
        data={entity?.title ? { id: entity.id, title: entity.title } : null}
        loading={result.fetching}
        error={result.error}
        onRetry={() => refresh({ requestPolicy: 'network-only' })}
        empty={<EmptyPlaceholder message="Deletion is unavailable." />}
      >
        {(entity) => (
          <DestructiveAction
            key={entity.id}
            confirmationName={entity.title}
            label="Delete team"
            submittingLabel="Deleting…"
            onAction={removeEntity}
            onSuccess={() => {
              if (organizationId) {
                dismissTo({
                  pathname: '/organization/teams',
                  params: { organizationId },
                });
                return;
              }
              dismissTo('/event');
            }}
          >
            <DestructiveActionHeading>
              Are you sure you want to delete {entity.title}?
            </DestructiveActionHeading>
            <DestructiveActionDescription>
              This cannot be undone.
            </DestructiveActionDescription>
          </DestructiveAction>
        )}
      </ContentBoundary>
    </>
  );
}
